/**
 * Turn a Table's columns + rows into plain string rows for `exportRowsToCsv`.
 *
 * WHY NOT MUI's built-in `apiRef.exportDataAsCsv()`
 * -------------------------------------------------
 * It exports the cell VALUE, not the rendered cell. Most columns in this app are
 * `renderCell`-only (rich React: CompanyCell, UserBlock, StatusChip, kebabs), and
 * their underlying field is frequently an object or an id. MUI would happily write
 * `[object Object]` or a raw uuid into the file and call it a successful export --
 * a silent wrong answer, which is worse than an obvious failure.
 *
 * So values are derived deliberately, in this precedence:
 *   1. `column.csvValue(row)`  -- explicit opt-in, always wins
 *   2. `valueFormatter(valueGetter(row))` -- MUI's own text path, correct for the
 *      date/currency/number columns Table already auto-injects converters for
 *   3. the raw field, if it is a primitive
 *   4. a best-effort label off a common-shaped object (`name`/`title`/`email`/...)
 *   5. empty string -- NEVER a stringified object
 *
 * A column that ends at step 5 exports blank. That is intentional: blank is
 * visibly missing, and the fix is to add a `csvValue` to that column. Guessing
 * would produce a file that looks complete and isn't.
 */

/** Columns that describe UI, not data — never exported. */
const EXCLUDED_FIELDS = new Set(['__select__', '__check__', 'actions', 'action', 'kebab'])

/** Keys worth trying when a cell's value is an object rather than a primitive. */
const LABEL_KEYS = ['name', 'title', 'label', 'email', 'sku', 'number', 'id']

const isExportable = (column: any): boolean => {
  if (!column || !column.field) return false
  if (EXCLUDED_FIELDS.has(column.field)) return false
  // VRG-734 — MUI's own `GridColDef.disableExport`, honoured here because this is the export path
  // (the grid's built-in one is not used). VERAfied guest notes are sensitive and set it.
  if (column.disableExport === true) return false
  // A column with no header is chrome (row actions, spacers), not a data column.
  if (column.headerName === undefined && column.csvValue === undefined) return false
  return true
}

const stringifyPrimitive = (v: unknown): string => {
  if (v === null || v === undefined) return ''
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? '' : v.toISOString()
  const t = typeof v
  if (t === 'string') return v as string
  if (t === 'number' || t === 'boolean' || t === 'bigint') return String(v)
  return ''
}

const cellText = (column: any, row: any): string => {
  // 1. explicit escape hatch
  if (typeof column.csvValue === 'function') {
    return stringifyPrimitive(column.csvValue(row)) || String(column.csvValue(row) ?? '')
  }

  const field = column.field
  let value: unknown = row?.[field]

  // 2. MUI's own value path. valueGetter/valueFormatter signatures vary across v7
  //    call shapes, so failures are swallowed -- a throwing formatter must not take
  //    the whole export down with it.
  if (typeof column.valueGetter === 'function') {
    try {
      value = column.valueGetter(value, row, column, undefined)
    } catch {
      /* fall through to the raw field */
    }
  }
  // Date columns skip the display formatter on purpose. Table auto-injects
  // `relativeOrDateTime` for `type: 'dateTime'`, which is right on screen ("in ~1
  // hour") and useless in a file — a spreadsheet cannot sort or filter it, and it
  // rots the moment it is saved. Export the absolute instant instead.
  if (column.type === 'dateTime' || column.type === 'date') {
    const asDate = value instanceof Date ? value : new Date(value as any)
    if (!Number.isNaN(asDate?.getTime?.())) return asDate.toISOString()
  }

  if (typeof column.valueFormatter === 'function') {
    try {
      const formatted = column.valueFormatter(value, row, column, undefined)
      const text = stringifyPrimitive(formatted)
      if (text !== '') return text
    } catch {
      /* fall through */
    }
  }

  // 3. primitive as-is
  const primitive = stringifyPrimitive(value)
  if (primitive !== '') return primitive

  // 4. best-effort label off a common-shaped object
  if (value && typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map(v => cellText({ field: '' }, { '': v }) || stringifyPrimitive(v)).filter(Boolean).join('; ')
    }
    for (const key of LABEL_KEYS) {
      const candidate = stringifyPrimitive((value as any)[key])
      if (candidate !== '') return candidate
    }
  }

  // 5. blank, never "[object Object]"
  return ''
}

/**
 * Build `{ header: text }` rows in the column order shown on screen.
 *
 * `visibleFields` (when given) restricts and orders the output to what the user can
 * actually see, so the file matches the table they are looking at rather than every
 * column the page defines.
 */
export function toCsvRows(
  columns: any[],
  rows: any[],
  visibleFields?: string[],
): Record<string, string>[] {
  const exportable = (columns ?? []).filter(isExportable)

  const ordered = visibleFields?.length
    ? visibleFields
        .map(f => exportable.find(c => c.field === f))
        .filter(Boolean)
    : exportable

  if (!ordered.length) return []

  // Duplicate headerNames would silently collapse into one CSV column, so they are
  // disambiguated with the field name.
  const seen = new Map<string, number>()
  const headers = ordered.map((c: any) => {
    const base = String(c.headerName || c.field)
    const n = (seen.get(base) ?? 0) + 1
    seen.set(base, n)
    return n === 1 ? base : `${base} (${c.field})`
  })

  return (rows ?? []).map(row => {
    const out: Record<string, string> = {}
    ordered.forEach((c: any, i: number) => {
      out[headers[i]] = cellText(c, row)
    })
    return out
  })
}
